<template>
  <el-col
    :span="24"
    id="ledger"
    v-loading.body="receiving && !navigating"
    element-loading-text="Fetching page...">
    <el-row type="flex" justify="start" align="middle" class="v-padded-s" id="ledger-header">
      <HeaderInfoText :colspan="9" title="LEDGER" :meta="meta"></HeaderInfoText>
      <el-col :span="6">
        <el-row type="flex" justify="center" v-if="meta.count > 20">
          <el-pagination
            layout="prev, pager, next, jumper"
            :total="meta.count"
            :page-size="20"
            @current-change="changePage"
            :current-page.sync="currentPage">
          </el-pagination>
        </el-row>
      </el-col>
      <el-col :span="9" class="h-padded right-aligned-col">
        <el-button plain @click="toggleFilters">
          {{ showFilters ? "Hide filters" : "Show filters" }}
        </el-button>
      </el-col>
    </el-row>
    <el-row type="flex" justify="center" align="middle" :gutter="20">
      <Filters
        :filters="filters"
        :filtersVisible="showFilters"
        clearAction="clearLedgerState"
        searchAction="fetchLedgersWithQuery"
        :autoApply="false">
        <template v-for="(f, i) in filters" :slot="`${f.type}${i}`" slot-scope="props">
          <component :key="i" :is="f.type" :properties="f.props" />
        </template>
      </Filters>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col :span="24">
        <el-table
          ref="ledgerTable"
          v-if="!receiving && meta.count"
          id="ledger-table"
          :data="ledger"
          border
          style="width: 100%; z-index: 0;"
          :maxHeight="tableHeight">
          <el-table-column prop="id" label="ID" align="center" width="75"></el-table-column>
          <el-table-column prop="type" label="Transaction Type" align="left" width="250">
            <template slot-scope="scope">
              <el-row type="flex" justify="start">
                {{ formatType(scope.row.type) }}
              </el-row>
            </template>
          </el-table-column>
          <el-table-column prop="descriptor" label="Description" align="left" fit></el-table-column>
          <el-table-column prop="bank" label="Bank Name" align="left" fit>
            <template slot-scope="scope">
              <el-row type="flex" justify="start">
                {{ getBankById(scope.row.bank) ? getBankById(scope.row.bank).name : '' }}
              </el-row>
            </template>
          </el-table-column>
        </el-table>
      </el-col>
    </el-row>
  </el-col>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { payload } from '@/components/RouteComponents/Freefields/actionPayload'
  import store from '@/store/store'
  import Filters from '@/components/Filters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import SelectFilter from '@/components/FilterComponents/SelectFilter'
  import AutocompleteFilter from '@/components/FilterComponents/AutocompleteFilter'
  import getUniqueValues from '@/utils/helpers/GetUniqueValues'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import { changePage } from '@/components/RouteComponents/commonFunctions'

  export default {
    components: {
      Filters,
      KeywordFilter,
      SelectFilter,
      AutocompleteFilter,
      HeaderInfoText
    },
    data () {
      return {
        filters: [
          {
            type: 'keywordFilter',
            props: {}
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'type',
              clearable: true,
              opts: [
                { label: 'Accounts Payable', value: 'accountspayable' },
                { label: 'Journal Entry', value: 'journalentry' },
                { label: 'Check', value: 'papercheck' },
                { label: 'Purchase Order', value: 'purchaseorder' },
                { label: 'Petty Cash', value: 'pettycash' }
              ],
              multi: false,
              placeholder: 'Type'
            }
          },
          {
            type: 'autocompleteFilter',
            props: {
              filterName: 'batch',
              remote: true,
              clearable: true,
              action: 'fetchBatchesWithQuery',
              clearAction: 'clearBatchState',
              getter: 'getBatchesBySearchCriteria',
              triggerOnFocus: false,
              placeholder: 'Batch',
              queryParamType: 'search',
              suggestionLabelAttr: 'name'
            }
          }
        ],
        tableHeight: 1000,
        showFilters: true,
        searchCriteria: null,
        currentPage: 1
      }
    },
    methods: {
      getUniqueValues,
      toggleFilters () {
        !this.showFilters ? this.showFilters = true : this.showFilters = false
      },
      formatType (unformattedType) {
        switch (unformattedType) {
          case 'journalentry':
            return 'Journal Entry'
          case 'accountspayable':
            return 'Accounts Payable'
          case 'purchaseorder':
            return 'Purchase Order'
          case 'pettycash':
            return 'Petty Cash'
          case 'papercheck':
            return 'Check'
        }
      },
      /**
       * Handles paginating between freefield pages
       * @param e  Target page
       */
      changePage (e) {
        changePage({ self: this, page: e, payload, query: null, getDeps: true })
      }
    },
    computed: {
      ...mapGetters({
        meta: 'getLedgerMetadata',
        ledger: 'getLedger',
        receiving: 'getLedgerFetchStatus',
        getBankById: 'getBankById',
        navigating: 'getNavigationStatus'
      })
    },
    beforeRouteEnter (to, from, next) {
      store.dispatch('clearLocalState', { clearMutation: payload.clearMutation, clearCache: true })
        .then(() => store.dispatch('fetchWithQuery', payload))
        .then(entities => store.dispatch('findDependencies', entities))
        .then(promises => Promise.all(promises))
        .then(() => next())
        .catch(e => console.log(e))
    },
    mounted () {
      const tableHeight = () => {
        const parentHeight = document.getElementById('ledger').offsetHeight
        const headerHeight = document.getElementById('ledger-header').offsetHeight
        return parentHeight - headerHeight
      }
      this.tableHeight = tableHeight()
    },
    beforeDestroy () {
      this.$store.dispatch('clearFilters')
    }
  }
</script>

<style scoped>
  #ledger-header {
    border-bottom: 1px solid lightgray;
  }
  .h-padded {
    padding: 0 12px;
  }
  .v-padded-s {
    padding: 12px 0;
  }
  .left-aligned-col {
    text-align: left;
  }
  .right-aligned-col {
    text-align: right;
  }
  .stats {
    margin: 0;
    display: inline-block;
    padding: 0 3px;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Ledger/Ledger.vue